import type { ReactNode } from "react";

// What a list page can be filtered by. Each spec names its URL params (or local
// state keys) and how its pill reads; `useFilters` turns specs into state and
// `FilterBar` into pills. Param names are the page's API to bookmarks and
// links, so a spec never renames them.

export interface FilterOption {
  value: string;
  label: string;
  /** Left of the label in the list (a flag, an icon). */
  icon?: ReactNode;
  /** Grey text right of the label (a count, a code). */
  hint?: ReactNode;
}

interface BaseSpec {
  key: string;
  label: string;
  /** Heading the filter sits under in the "+ Filter" menu. */
  group?: string;
  /** Always shown as a pill, even when empty. */
  pinned?: boolean;
}

/** Pick one or several values from a list (static, loaded, or typed in). */
export interface OptionsSpec extends BaseSpec {
  kind: "options";
  param: string;
  /** Several values (checkboxes); false for one (a radio list). Default true. */
  multiple?: boolean;
  options?: FilterOption[];
  /** Server-side search; the list shows its results for what is typed. */
  loadOptions?: (query: string) => Promise<FilterOption[]>;
  /** Any typed text can be added as a value. */
  freeSolo?: boolean;
  loading?: boolean;
  /** `comma` (default): `?country=NO,SE`; `repeat`: `?role=a&role=b`. */
  encoding?: "comma" | "repeat";
  /**
   * The values in force while the param is absent. Clearing the filter returns
   * here; a different choice is written to the URL.
   */
  default?: string[] | ((get: (param: string) => string | null) => string[]);
  /** Written when the user picks nothing while there is a default (`min_rating=all`). */
  noneValue?: string;
  /** Pill text for "nothing picked" when `noneValue` is in force ("Any"). */
  noneLabel?: string;
  /** A single choice that always has a value: no "Any" row. Give it a `default`. */
  required?: boolean;
  searchPlaceholder?: string;
}

/** Any / yes / no. */
export interface BooleanSpec extends BaseSpec {
  kind: "boolean";
  param: string;
  /** How yes and no read on the pill and in the editor. */
  labels?: { yes: string; no: string };
  /** URL values for yes and no. Default `yes` / `no`. */
  values?: { yes: string; no: string };
  /** Only "yes" or nothing (a switch-like filter: "Has email"). */
  yesOnly?: boolean;
}

/** Numeric bounds, typed (settles before it reaches the URL). */
export interface RangeSpec extends BaseSpec {
  kind: "range";
  minParam: string;
  /** Leave out for a min-only filter ("At least"). */
  maxParam?: string;
  unit?: string;
  decimal?: boolean;
}

export interface DatePreset {
  label: string;
  /** Days back from today (0 = today). */
  days?: number;
  /** Calendar periods. */
  period?: "month" | "year";
}

/** From / to dates (`YYYY-MM-DD`), with quick presets. */
export interface DateRangeSpec extends BaseSpec {
  kind: "dateRange";
  fromParam: string;
  toParam?: string;
  presets?: DatePreset[];
}

/** Free text, typed (settles before it reaches the URL). */
export interface TextSpec extends BaseSpec {
  kind: "text";
  param: string;
  placeholder?: string;
}

export interface CustomEditorContext {
  get: (param: string) => string | null;
  set: (updates: Record<string, string | null>) => void;
  close: () => void;
}

/** Anything else: a special picker (CPV codes), or a filter only links set. */
export interface CustomSpec extends BaseSpec {
  kind: "custom";
  params: string[];
  /** The pill's value text; null while unset. */
  summary: (get: (param: string) => string | null) => string | null;
  /**
   * Set apart from its default (tinted, counted, cleared by "Clear all").
   * Default: while `summary` isn't null. Lets a pill show its default value
   * quietly ("Month: October 2026").
   */
  active?: (get: (param: string) => string | null) => boolean;
  /** The popover's content; leave out for a pill that can only be removed. */
  renderEditor?: (ctx: CustomEditorContext) => ReactNode;
}

export type FilterSpec = OptionsSpec | BooleanSpec | RangeSpec | DateRangeSpec | TextSpec | CustomSpec;

export const DEFAULT_DATE_PRESETS: DatePreset[] = [
  { label: "Today", days: 0 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "This month", period: "month" },
  { label: "This year", period: "year" },
];

/** Every URL param (or local key) a spec owns. */
export function specParams(spec: FilterSpec): string[] {
  switch (spec.kind) {
    case "options":
    case "boolean":
    case "text":
      return [spec.param];
    case "range":
      return spec.maxParam ? [spec.minParam, spec.maxParam] : [spec.minParam];
    case "dateRange":
      return spec.toParam ? [spec.fromParam, spec.toParam] : [spec.fromParam];
    case "custom":
      return spec.params;
  }
}

/** The params a spec types into (debounced on their way to the URL). */
export function typedParams(spec: FilterSpec): string[] {
  if (spec.kind === "text") return [spec.param];
  if (spec.kind === "range") return specParams(spec);
  return [];
}

const pad = (n: number) => String(n).padStart(2, "0");
export const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** The `from` date a preset means today. */
export function presetFrom(preset: DatePreset, today = new Date()): string {
  const d = new Date(today);
  if (preset.period === "month") d.setDate(1);
  else if (preset.period === "year") d.setMonth(0, 1);
  else d.setDate(d.getDate() - (preset.days ?? 0));
  return isoDate(d);
}
